import { bus } from './event-bus.js';
import { store } from './state-store.js';
import { initFullscreen } from './fullscreen.js';

const ALL_EVENTS = [
  'adloaded', 'addurationchange', 'adclick', 'adclosed', 'adimpression',
  'adcreativeview', 'adcollapse', 'adstarted', 'adtagloaded', 'adprogress',
  'adviewable', 'adviewundetermined', 'adinitialplayrequestfailed',
  'adinitialplayrequestsucceeded', 'adpaused', 'adresumed', 'adtagstartloading',
  'advolumemuted', 'advolumeunmuted', 'advolumechanged', 'adskipped',
  'adskippablestatechanged', 'adfirstquartile', 'admidpoint', 'adthirdquartile',
  'adcomplete', 'adplayerexpand', 'adplayercollapse', 'adfullscreen',
  'adexitfullscreen', 'adiconclick', 'aderror', 'addestroyed', 'adpodcompleted',
  'adtrackingcomplete', 'adtrackingeventsloaded',
  // VPAID events
  'adlinearchange', 'adsizechange', 'adexpandedchange',
  'adremainingtimechange', 'adinteraction', 'aduseracceptinvitation',
];

const EVENT_CATEGORIES = {
  adloaded: 'lifecycle', addurationchange: 'lifecycle', adstarted: 'lifecycle',
  adtagloaded: 'lifecycle', adtagstartloading: 'lifecycle', addestroyed: 'lifecycle',
  adclosed: 'lifecycle', adpodcompleted: 'lifecycle', adtrackingcomplete: 'lifecycle',
  adtrackingeventsloaded: 'lifecycle',

  adprogress: 'progress', adfirstquartile: 'progress', admidpoint: 'progress',
  adthirdquartile: 'progress', adcomplete: 'progress',

  aderror: 'error', adinitialplayrequestfailed: 'error',

  adclick: 'interaction', adiconclick: 'interaction', adcreativeview: 'interaction',
  adimpression: 'interaction', adviewable: 'interaction', adviewundetermined: 'interaction',
  adinitialplayrequestsucceeded: 'interaction',

  adpaused: 'volume', adresumed: 'volume', advolumemuted: 'volume',
  advolumeunmuted: 'volume', advolumechanged: 'volume',

  adskipped: 'interaction', adskippablestatechanged: 'lifecycle',
  adplayerexpand: 'lifecycle', adplayercollapse: 'lifecycle',
  adfullscreen: 'lifecycle', adexitfullscreen: 'lifecycle',
  adcollapse: 'lifecycle',

  adlinearchange: 'lifecycle', adsizechange: 'lifecycle', adexpandedchange: 'lifecycle',
  adremainingtimechange: 'progress', adinteraction: 'interaction',
  aduseracceptinvitation: 'interaction',
};

const AUDIO_SAMPLE_INTERVAL_MS = 50;
const AUDIO_STARTUP_WINDOW_MS = 2000;
const AUDIO_LEVEL_THRESHOLD = 0.02;
const AUDIO_STARTUP_DETECTION_DWELL_MS = 150;
const AUDIO_STEADY_DETECTION_DWELL_MS = 500;

export class PlayerManager {
  constructor() {
    this._rmpVast = null;
    this._fullscreen = null;
    this._snapshotInterval = null;
    this._updateInterval = null;
    this._initialized = false;
    this._params = {};
    this._containerId = 'vast-player';
    this._pendingLoad = null;
    this._audioContext = null;
    this._audioAnalyser = null;
    this._audioSourceNode = null;
    this._audioBoundElement = null;
    this._audioSampleBuffer = null;
    this._audioAboveThresholdMs = 0;
    this._audioDetectionStartedAtMs = 0;
  }

  get rmpVast() {
    return this._rmpVast;
  }

  get isInitialized() {
    return this._initialized;
  }

  async init(params = {}) {
    if (!window.RmpVast) {
      throw new Error('RmpVast not loaded. Ensure rmp-vast.js is included via <script> tag.');
    }
    store.clearSnapshots();
    this.destroy();

    this._params = params;
    this._pendingLoad = null;
    this._resetAudioDetection();
    const container = document.getElementById(this._containerId);
    if (!container) {
      throw new Error(`Container #${this._containerId} not found`);
    }

    const muted = params.muted !== undefined ? Boolean(params.muted) : false;
    this._applyContentVideoMutedState(muted);

    const rmpVastParams = {
      ajaxTimeout: params.ajaxTimeout ?? 8000,
      creativeLoadTimeout: params.creativeLoadTimeout ?? 10000,
      ajaxWithCredentials: params.ajaxWithCredentials ?? false,
      maxNumRedirects: params.maxNumRedirects ?? 10,
      outstream: params.outstream ?? false,
      showControlsForAdPlayer: params.showControlsForAdPlayer ?? true,
      enableSimid: params.enableSimid ?? true,
      enableVpaid: params.enableVpaid ?? true,
      vpaidSettings: params.vpaidSettings ?? {
        width: params.playerWidth ?? 960,
        height: params.playerHeight ?? 540,
        viewMode: params.vpaidViewMode ?? 'normal',
        desiredBitrate: 500,
      },
      useHlsJS: params.useHlsJS ?? true,
      debugHlsJS: false,
      vastXmlInput: params.vastXmlInput ?? false,
      labels: {
        skipMessage: 'Skip ad',
        closeAd: 'Close ad',
        textForInteractionUIOnMobile: 'Learn more',
      },
    };

    this._rmpVast = new window.RmpVast(this._containerId, rmpVastParams);
    this._initialized = true;
    this._applyPlayerVolumeState(muted);

    this._fullscreen = initFullscreen(
      container,
      this._rmpVast,
      params.playerWidth ?? 960,
      params.playerHeight ?? 540
    );

    this._wireEvents();
    this._startStateUpdates();

    bus.emit('player:initialized', { params: rmpVastParams });
  }

  async loadAds(tagUrl, meta = {}) {
    if (!this._rmpVast) {
      console.warn('[PlayerManager] Not initialized. Call init() first.');
      return;
    }

    await this._prepareForNewLoad({ vastXmlInput: false });

    if (this._params.autoplay === false && !meta.forceStart) {
      this._pendingLoad = {
        type: 'tag',
        value: tagUrl,
        meta,
      };
      store.update({
        playerState: 'ready',
        sessionSourceType: meta.sourceType || 'tag',
        sessionSourceValue: meta.sourceValue || tagUrl,
        adTagUrl: tagUrl,
        rawXml: null,
      });
      this._updatePlayerStateAttr('ready');
      bus.emit('player:queued', { type: 'tag', value: tagUrl });
      return;
    }

    store.startSession({
      sourceType: meta.sourceType || 'tag',
      sourceValue: meta.sourceValue || tagUrl,
      tag: tagUrl,
    });
    store.update({
      playerState: 'loading',
      adTagUrl: tagUrl,
      rawXml: null,
    });

    this._updatePlayerStateAttr('loading');
    this._pendingLoad = null;

    this._rmpVast.loadAds(tagUrl);
  }

  async loadRawXml(xmlString, meta = {}) {
    if (!this._rmpVast) {
      console.warn('[PlayerManager] Not initialized. Call init() first.');
      return;
    }

    await this._prepareForNewLoad({ vastXmlInput: true });

    if (this._params.autoplay === false && !meta.forceStart) {
      this._pendingLoad = {
        type: 'xml',
        value: xmlString,
        meta,
      };
      store.update({
        playerState: 'ready',
        sessionSourceType: meta.sourceType || 'xml',
        sessionSourceValue: meta.sourceValue || '(raw XML)',
        adTagUrl: '(raw XML)',
        rawXml: xmlString,
      });
      this._updatePlayerStateAttr('ready');
      bus.emit('player:queued', { type: 'xml', value: '(raw XML)' });
      return;
    }

    store.startSession({
      sourceType: meta.sourceType || 'xml',
      sourceValue: meta.sourceValue || '(raw XML)',
      rawXml: true,
    });
    store.update({
      playerState: 'loading',
      adTagUrl: '(raw XML)',
      rawXml: xmlString,
    });

    this._updatePlayerStateAttr('loading');
    this._pendingLoad = null;

    this._rmpVast.loadAds(xmlString);
  }

  async play() {
    if (this._pendingLoad) {
      const pending = this._pendingLoad;
      this._pendingLoad = null;
      if (pending.type === 'xml') {
        await this.loadRawXml(pending.value, { ...pending.meta, forceStart: true });
      } else {
        await this.loadAds(pending.value, { ...pending.meta, forceStart: true });
      }
      return;
    }
    this._rmpVast?.play();
  }

  pause() {
    this._rmpVast?.pause();
  }

  stop() {
    this._rmpVast?.stopAds();
  }

  skip() {
    this._rmpVast?.skipAd();
  }

  toggleFullscreen() {
    this._fullscreen?.toggle();
  }

  set volume(val) {
    if (this._rmpVast) {
      this._rmpVast.volume = val;
    }
  }

  get volume() {
    return this._rmpVast?.volume ?? 1;
  }

  set muted(val) {
    const muted = Boolean(val);
    this._applyContentVideoMutedState(muted);
    if (this._rmpVast) {
      this._rmpVast.muted = muted;
    }
    store.update({
      muted,
      volume: muted ? 0 : (this._rmpVast?.volume ?? store.state.volume ?? 1),
    });
  }

  get muted() {
    return this._rmpVast?.muted ?? false;
  }

  destroy() {
    if (this._rmpVast) {
      try {
        this._rmpVast.destroy();
      } catch {
        // ignore
      }
      this._rmpVast = null;
    }
    if (this._fullscreen) {
      this._fullscreen.destroy();
      this._fullscreen = null;
    }
    this._stopStateUpdates();
    this._disposeAudioGraph();
    this._resetAudioDetection();
    this._pendingLoad = null;
    this._initialized = false;
  }

  readAllGetters() {
    const rv = this._rmpVast;
    if (!rv) return {};

    const getters = [
      'adPaused', 'volume', 'muted', 'adTagUrl', 'adMediaUrl', 'adLinear',
      'adSystem', 'adUniversalAdIds', 'adContentType', 'adTitle', 'adDescription',
      'adAdvertiser', 'adPricing', 'adSurvey', 'adAdServingId', 'adCategories',
      'adBlockedAdCategories', 'adDuration', 'adCurrentTime', 'adRemainingTime',
      'adOnStage', 'adMediaWidth', 'adMediaHeight', 'clickThroughUrl',
      'skipTimeOffset', 'isSkippableAd', 'adSkippableState', 'initialized',
      'contentPlayerCompleted',
    ];

    const result = {};
    for (const key of getters) {
      try {
        result[key] = rv[key];
      } catch {
        result[key] = undefined;
      }
    }
    result.audioSignalDetected = store.state.audioSignalDetected;
    result.audioSignalLevel = store.state.audioSignalLevel;
    return result;
  }

  _wireEvents() {
    const rv = this._rmpVast;
    if (!rv) return;

    for (const eventName of ALL_EVENTS) {
      rv.on(eventName, (event) => this._handleAdEvent(event));
    }
  }

  _handleAdEvent(event) {
    if (!event?.type) return;
    const type = event.type;
    const category = EVENT_CATEGORIES[type] || 'lifecycle';
    const rv = this._rmpVast;

    let extraData = {};

    if (type === 'aderror' && rv) {
      extraData = {
        message: rv.adErrorMessage,
        code: rv.adVastErrorCode,
        errorType: rv.adErrorType,
      };
    }

    store.addEvent(type, { ...extraData, category });

    // Update state based on event
    switch (type) {
      case 'adtagstartloading':
        store.update({ playerState: 'loading' });
        this._updatePlayerStateAttr('loading');
        break;
      case 'adstarted':
        this._resetAudioDetection();
        this._audioDetectionStartedAtMs = performance.now();
        store.update({
          playerState: 'playing',
          adOnStage: true,
          adType: rv?.adLinear ? 'linear' : 'nonlinear',
        });
        this._updatePlayerStateAttr('playing');
        this._syncGetterState();
        break;
      case 'adpaused':
        store.update({ playerState: 'paused', adPaused: true });
        this._updatePlayerStateAttr('paused');
        break;
      case 'adresumed':
        store.update({ playerState: 'playing', adPaused: false });
        this._updatePlayerStateAttr('playing');
        break;
      case 'adcomplete':
      case 'addestroyed':
        this._disposeAudioGraph();
        this._freezeTerminalState({
          playerState: 'complete',
          adOnStage: false,
        });
        store.finishSession('complete');
        this._updatePlayerStateAttr('complete');
        break;
      case 'aderror':
        this._disposeAudioGraph();
        this._freezeTerminalState({
          playerState: 'error',
          adOnStage: false,
          adErrorMessage: extraData.message || '',
          adVastErrorCode: extraData.code ?? -1,
          adErrorType: extraData.errorType || '',
        });
        store.finishSession('error');
        this._updatePlayerStateAttr('error');
        break;
      case 'adskippablestatechanged':
        if (rv) store.update({ adSkippableState: rv.adSkippableState });
        break;
    }

    bus.emit('ad:event', { type, category, data: extraData, timestamp: store.events.at(-1)?.timestamp });
  }

  _syncGetterState() {
    const vals = this.readAllGetters();
    this._captureLiveSnapshot(vals);
    store.update({
      adDuration: vals.adDuration ?? -1,
      adMediaUrl: vals.adMediaUrl ?? '',
      adContentType: vals.adContentType ?? '',
      adTitle: vals.adTitle ?? '',
      adSystem: vals.adSystem ?? null,
      adDescription: vals.adDescription ?? '',
      adMediaWidth: vals.adMediaWidth ?? -1,
      adMediaHeight: vals.adMediaHeight ?? -1,
      clickThroughUrl: vals.clickThroughUrl ?? '',
      isSkippableAd: vals.isSkippableAd ?? false,
      skipTimeOffset: vals.skipTimeOffset ?? -1,
      adLinear: vals.adLinear ?? true,
    });
  }

  _startStateUpdates() {
    this._updateInterval = setInterval(() => {
      if (!this._rmpVast?.adOnStage) return;
      const vals = this.readAllGetters();
      const audioSignal = this._sampleAudioSignal(vals);
      vals.audioSignalDetected = audioSignal.detected;
      vals.audioSignalLevel = audioSignal.level;
      this._captureLiveSnapshot(vals);
      store.update({
        adCurrentTime: vals.adCurrentTime ?? -1,
        adRemainingTime: vals.adRemainingTime ?? -1,
        volume: vals.volume ?? 1,
        muted: vals.muted ?? false,
        adPaused: vals.adPaused ?? false,
        audioSignalDetected: audioSignal.detected,
        audioSignalLevel: audioSignal.level,
      });
      bus.emit('player:tick', vals);
    }, AUDIO_SAMPLE_INTERVAL_MS);

    this._snapshotInterval = setInterval(() => {
      if (this._rmpVast?.adOnStage) {
        store.takeSnapshot();
      }
    }, 500);
  }

  _stopStateUpdates() {
    clearInterval(this._updateInterval);
    clearInterval(this._snapshotInterval);
    this._updateInterval = null;
    this._snapshotInterval = null;
  }

  async _prepareForNewLoad({ vastXmlInput = false } = {}) {
    const environment = store.state.environment;
    const shouldReinitialize = !this._rmpVast ||
      (this._params.vastXmlInput ?? false) !== vastXmlInput ||
      Boolean(this._pendingLoad) ||
      Boolean(this._rmpVast?.adOnStage) ||
      store.state.playerState !== 'idle' ||
      store.state.sessionStatus !== 'idle';

    if (shouldReinitialize) {
      await this.init({ ...this._params, vastXmlInput });
    } else {
      this._pendingLoad = null;
      this._disposeAudioGraph();
      this._resetAudioDetection();
    }

    store.reset();
    store.update({ environment });
    bus.emit('session:reset');
    this._updatePlayerStateAttr('idle');
  }

  _sampleAudioSignal(vals = {}) {
    const isAudibleContext = Boolean(vals.adOnStage) &&
      vals.adPaused !== true &&
      vals.muted !== true &&
      Number(vals.volume ?? 0) > 0;

    if (!isAudibleContext) {
      this._audioAboveThresholdMs = 0;
      return {
        detected: Boolean(store.state.audioSignalDetected),
        level: 0,
      };
    }

    if (!this._bindAudioGraphToAdPlayer()) {
      this._audioAboveThresholdMs = 0;
      return {
        detected: Boolean(store.state.audioSignalDetected),
        level: 0,
      };
    }

    try {
      this._audioAnalyser.getByteTimeDomainData(this._audioSampleBuffer);
    } catch {
      return {
        detected: Boolean(store.state.audioSignalDetected),
        level: 0,
      };
    }

    const level = this._computeNormalizedRms(this._audioSampleBuffer);
    const elapsedMs = this._audioDetectionStartedAtMs > 0
      ? Math.max(0, performance.now() - this._audioDetectionStartedAtMs)
      : Number.POSITIVE_INFINITY;
    const inStartupWindow = elapsedMs <= AUDIO_STARTUP_WINDOW_MS;
    const requiredDwellMs = inStartupWindow
      ? AUDIO_STARTUP_DETECTION_DWELL_MS
      : AUDIO_STEADY_DETECTION_DWELL_MS;

    if (level >= AUDIO_LEVEL_THRESHOLD) {
      this._audioAboveThresholdMs += AUDIO_SAMPLE_INTERVAL_MS;
    } else {
      this._audioAboveThresholdMs = 0;
    }

    const detected = Boolean(store.state.audioSignalDetected) ||
      this._audioAboveThresholdMs >= requiredDwellMs;

    return { detected, level };
  }

  _bindAudioGraphToAdPlayer() {
    const rv = this._rmpVast;
    const mediaElement = rv?.adPlayer;
    if (!mediaElement || typeof mediaElement !== 'object') return false;

    // Routing a cross-origin media element through createMediaElementSource()
    // makes the Web Audio API output silence (cross-origin protection), which
    // would mute the ad entirely. Skip analysis in that case so audio stays
    // audible; detection is simply unavailable for cross-origin creatives.
    if (!this._canAnalyzeMediaElementAudio(mediaElement)) {
      return false;
    }

    if (this._audioBoundElement === mediaElement &&
      this._audioAnalyser &&
      this._audioSampleBuffer) {
      return true;
    }

    this._disposeAudioGraph();

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return false;

    try {
      this._audioContext = new AudioContextCtor();
      if (this._audioContext.state === 'suspended') {
        void this._audioContext.resume().catch(() => {});
      }

      this._audioSourceNode = this._audioContext.createMediaElementSource(mediaElement);
      this._audioAnalyser = this._audioContext.createAnalyser();
      this._audioAnalyser.fftSize = 2048;
      this._audioAnalyser.smoothingTimeConstant = 0.2;
      this._audioSampleBuffer = new Uint8Array(this._audioAnalyser.fftSize);

      this._audioSourceNode.connect(this._audioAnalyser);
      this._audioAnalyser.connect(this._audioContext.destination);
      this._audioBoundElement = mediaElement;
      return true;
    } catch {
      this._disposeAudioGraph();
      return false;
    }
  }

  _canAnalyzeMediaElementAudio(mediaElement) {
    const src = mediaElement?.currentSrc || mediaElement?.src || '';
    if (!src) return false;

    // CORS-enabled elements can be analyzed safely even cross-origin.
    if (mediaElement.crossOrigin === 'anonymous' || mediaElement.crossOrigin === 'use-credentials') {
      return true;
    }

    try {
      const mediaUrl = new URL(src, window.location.href);
      // blob:/data: URLs created by the page (e.g. hls.js) are same-origin.
      if (mediaUrl.protocol === 'blob:' || mediaUrl.protocol === 'data:') {
        return true;
      }
      return mediaUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  _disposeAudioGraph() {
    if (this._audioSourceNode) {
      try {
        this._audioSourceNode.disconnect();
      } catch {
        // ignore
      }
    }
    if (this._audioAnalyser) {
      try {
        this._audioAnalyser.disconnect();
      } catch {
        // ignore
      }
    }
    if (this._audioContext) {
      try {
        this._audioContext.close();
      } catch {
        // ignore
      }
    }
    this._audioContext = null;
    this._audioAnalyser = null;
    this._audioSourceNode = null;
    this._audioBoundElement = null;
    this._audioSampleBuffer = null;
  }

  _resetAudioDetection() {
    this._audioAboveThresholdMs = 0;
    this._audioDetectionStartedAtMs = 0;
    store.update({
      audioSignalDetected: false,
      audioSignalLevel: 0,
    });
  }

  _computeNormalizedRms(samples) {
    if (!samples?.length) return 0;
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const centered = (samples[i] - 128) / 128;
      sumSquares += centered * centered;
    }
    return Math.sqrt(sumSquares / samples.length);
  }

  _applyPlaybackProfile(params = {}) {
    const muted = params.muted !== undefined ? Boolean(params.muted) : false;
    this._applyContentVideoMutedState(muted);
    this._applyPlayerVolumeState(muted);
  }

  _applyContentVideoMutedState(muted) {
    const contentVideo = document.getElementById('rmp-video');
    if (!contentVideo) return;

    contentVideo.muted = muted;
    if (muted) {
      contentVideo.setAttribute('muted', '');
    } else {
      contentVideo.removeAttribute('muted');
    }
  }

  _applyPlayerVolumeState(muted) {
    if (this._rmpVast) {
      this._rmpVast.muted = muted;
      if (!muted) {
        const currentVolume = this._rmpVast.volume;
        if (!Number.isFinite(currentVolume) || currentVolume === 0) {
          this._rmpVast.volume = 1;
        }
      }
    }

    const volume = muted ? 0 : (this._rmpVast?.volume ?? 1);
    store.update({ muted, volume });
  }

  _updatePlayerStateAttr(state) {
    const el = document.getElementById(this._containerId);
    if (el) {
      el.setAttribute('data-player-state', state);
    }
    const badge = document.querySelector('[data-testid="player-state-badge"]');
    if (badge) {
      badge.textContent = state.toUpperCase();
      badge.setAttribute('data-state', state);
    }
  }

  _captureLiveSnapshot(vals = {}) {
    store.captureLiveSnapshot({
      ...vals,
      ...this._getStoreBackedSnapshot(),
      initialized: this._initialized,
    });
  }

  _freezeTerminalState(partial = {}) {
    const liveVals = this.readAllGetters();
    const frozenSnapshot = {
      ...(store.lastLiveSnapshot || {}),
      ...liveVals,
      ...this._getStoreBackedSnapshot(),
      ...partial,
      initialized: this._initialized,
    };

    store.captureLiveSnapshot(frozenSnapshot);
    store.freezeFinalSnapshot(frozenSnapshot);
    store.update({
      ...this._getStateFieldsFromSnapshot(frozenSnapshot),
      ...partial,
    });
  }

  _getStoreBackedSnapshot() {
    const state = store.state;
    return {
      adPaused: state.adPaused,
      volume: state.volume,
      muted: state.muted,
      adDuration: state.adDuration,
      adCurrentTime: state.adCurrentTime,
      adRemainingTime: state.adRemainingTime,
      adTagUrl: state.adTagUrl,
      adMediaUrl: state.adMediaUrl,
      adContentType: state.adContentType,
      adTitle: state.adTitle,
      adSystem: state.adSystem,
      adDescription: state.adDescription,
      adMediaWidth: state.adMediaWidth,
      adMediaHeight: state.adMediaHeight,
      clickThroughUrl: state.clickThroughUrl,
      isSkippableAd: state.isSkippableAd,
      skipTimeOffset: state.skipTimeOffset,
      adLinear: state.adLinear,
      audioSignalDetected: state.audioSignalDetected,
      audioSignalLevel: state.audioSignalLevel,
      adSkippableState: state.adSkippableState,
      adOnStage: state.adOnStage,
      adErrorMessage: state.adErrorMessage,
      adVastErrorCode: state.adVastErrorCode,
      adErrorType: state.adErrorType,
    };
  }

  _getStateFieldsFromSnapshot(snapshot) {
    const next = {};
    for (const key of [
      'adPaused', 'volume', 'muted', 'adDuration', 'adCurrentTime', 'adRemainingTime',
      'adTagUrl', 'adMediaUrl', 'adContentType', 'adTitle', 'adSystem',
      'adDescription', 'adMediaWidth', 'adMediaHeight', 'clickThroughUrl',
      'isSkippableAd', 'skipTimeOffset', 'adLinear', 'adSkippableState',
      'audioSignalDetected', 'audioSignalLevel',
      'adErrorMessage', 'adVastErrorCode', 'adErrorType',
    ]) {
      if (snapshot[key] !== undefined) {
        next[key] = snapshot[key];
      }
    }
    return next;
  }
}

export const playerManager = new PlayerManager();
